import { App as AntApp, Button, ConfigProvider, Result, theme } from 'antd';
import arEG from 'antd/locale/ar_EG';
import {
  type ComponentType,
  createContext,
  StrictMode,
  useContext,
} from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'urql';
import { login, type Session } from './auth';
import { createClient } from './client';
import { config } from './env';
import { showWholeDirhams } from './format';
import { hasAnyRole, type Role } from './roles';
import './base.css';

const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) throw new Error('useSession outside startApp');
  return session;
}

// Large, high-contrast defaults: the apps are used on phones, outdoors, by
// people with weak eyesight.
const appTheme = {
  algorithm: theme.defaultAlgorithm,
  token: {
    fontSize: 18,
    fontSizeLG: 20,
    fontSizeHeading2: 28,
    controlHeight: 48,
    controlHeightLG: 60,
    borderRadius: 10,
    colorPrimary: '#0b6bcb',
    // antd's greys are too faint outdoors: secondary text and descriptions
    // as dark as `.field-hint` (doc 52).
    colorTextSecondary: '#444',
    colorTextDescription: '#555',
    fontFamily:
      "'Noto Sans Arabic', 'Noto Naskh Arabic', Tahoma, system-ui, sans-serif",
  },
};

// The desk (doc 30): a screen and a mouse, many rows at once. Still larger
// than antd's defaults (16 px), for the same users.
const deskTheme = {
  ...appTheme,
  token: {
    ...appTheme.token,
    fontSize: 16,
    fontSizeLG: 17,
    fontSizeHeading2: 24,
    controlHeight: 38,
    controlHeightLG: 44,
    borderRadius: 8,
  },
};

type Density = 'phone' | 'desk';

function Shell({
  children,
  density = 'phone',
}: {
  children: React.ReactNode;
  density?: Density;
}) {
  return (
    <ConfigProvider
      direction="rtl"
      locale={arEG}
      theme={density === 'desk' ? deskTheme : appTheme}
    >
      <AntApp>{children}</AntApp>
    </ConfigProvider>
  );
}

type StartOptions = {
  /** Roles that may use this app (checked only when VITE_ENFORCE_ROLES=true). */
  allow: readonly Role[];
  App: ComponentType;
  /** 'desk': a denser theme for the desktop app (doc 30). */
  density?: Density;
};

export async function startApp({ allow, App, density }: StartOptions) {
  const root = createRoot(document.getElementById('root') as HTMLElement);
  // Phones show "80 درهم", the desk "80,00 درهم" (doc 52).
  showWholeDirhams(density !== 'desk');

  // Installable on phones (see public/sw.js: it caches nothing).
  if (
    density !== 'desk' &&
    import.meta.env.PROD &&
    'serviceWorker' in navigator
  ) {
    navigator.serviceWorker
      .register(`${import.meta.env.BASE_URL}sw.js`)
      .catch((error) => console.warn('Service worker not registered', error));
  }

  if (!config.graphqlUrl) {
    root.render(
      <Shell>
        <Result
          status="error"
          title="إعدادات ناقصة"
          subTitle="VITE_GRAPHQL_API is missing from .env"
        />
      </Shell>,
    );
    return;
  }

  let session: Session;
  try {
    session = await login();
  } catch (error) {
    console.error('Login failed', error);
    root.render(
      <Shell>
        <Result
          status="error"
          title="تعذّر تسجيل الدخول"
          subTitle="تحقّق من الاتصال بالإنترنت ثم أعد تحميل الصفحة."
        />
      </Shell>,
    );
    return;
  }

  if (config.enforceRoles && !hasAnyRole(session.roles, allow)) {
    root.render(
      <Shell>
        <Result
          status="403"
          title="لا تملك صلاحية استعمال هذا التطبيق"
          subTitle={`الحساب: ${session.username}. اطلب الصلاحية من اللجنة.`}
          extra={
            <Button type="link" onClick={session.logout}>
              تسجيل الخروج
            </Button>
          }
        />
      </Shell>,
    );
    return;
  }

  const client = createClient();
  root.render(
    <StrictMode>
      <Shell density={density}>
        <SessionContext.Provider value={session}>
          <Provider value={client}>
            <App />
          </Provider>
        </SessionContext.Provider>
      </Shell>
    </StrictMode>,
  );
}
